import { getApiClient } from './authApiClient';

const client = () => getApiClient('tenant');

export const getDistributors = async () => (await client().get('/distributors')).data;
export const getDistributor = async (id) => (await client().get(`/distributors/${id}`)).data;
export const createDistributor = async (data) => (await client().post('/distributors', data)).data;
export const updateDistributor = async (id, data) => (await client().put(`/distributors/${id}`, data)).data;
export const deleteDistributor = async (id) => (await client().delete(`/distributors/${id}`)).data;
export const assignDistributorClient = async (id, tenantId) =>
  (await client().post(`/distributors/${id}/clients`, { tenantId })).data;
export const unassignDistributorClient = async (id, tenantId) =>
  (await client().delete(`/distributors/${id}/clients/${tenantId}`)).data;

// Admin: (re)provision the distributor's login + (re)send the welcome/credentials email.
export const inviteDistributor = async (id) =>
  (await client().post(`/distributors/${id}/invite`)).data;
export const getDistributorUsers = async (id) =>
  (await client().get(`/distributors/${id}/users`)).data;
export const inviteDistributorUser = async (id, data) =>
  (await client().post(`/distributors/${id}/users`, data)).data;
export const deleteDistributorUser = async (id, userId) =>
  (await client().delete(`/distributors/${id}/users/${encodeURIComponent(userId)}`)).data;
export const getDistributorPortal = async (id) =>
  (await client().get(`/distributors/${id}/portal`)).data;

// Distributor self-service (the logged-in partner).
export const getMyDistributor = async () => (await client().get('/distributors/me')).data;
export const getMyDistributorClients = async () => (await client().get('/distributors/me/clients')).data;
export const createMyDistributorClient = async (data) => (await client().post('/distributors/me/clients', data)).data;
export const createDistributorClient = async (id, data) => (await client().post(`/distributors/${id}/clients/create`, data)).data;
export const updateMyDistributorClientApplicant = async (tenantId, profile) =>
  (await client().put(`/distributors/me/clients/${tenantId}/applicant`, profile)).data;
export const updateDistributorClientApplicant = async (id, tenantId, profile) =>
  (await client().put(`/distributors/${id}/clients/${tenantId}/applicant`, profile)).data;
export const updateMyDistributorTemplate = async (template) =>
  (await client().put('/distributors/me/template', { template })).data;

// Distributor insurance workspace. Admin preview uses the same API with an explicit distributor
// id; the backend applies the identical book/tenant isolation checks to both paths.
const workspaceBase = (distributorId) => distributorId
  ? `/distributors/${distributorId}/workspace`
  : '/distributors/me/workspace';
export const listDistributorWorkspace = async ({ distributorId, type, tenantId, status } = {}) =>
  (await client().get(workspaceBase(distributorId), { params: { type, tenantId, status } })).data;
export const listDistributorAudit = async ({ distributorId, tenantId } = {}) =>
  (await client().get(`${workspaceBase(distributorId)}/audit`, { params: { tenantId } })).data;
export const createDistributorWorkspaceEntity = async (type, data, distributorId) =>
  (await client().post(`${workspaceBase(distributorId)}/${type}`, data)).data;
export const updateDistributorWorkspaceEntity = async (type, id, data, distributorId) =>
  (await client().put(`${workspaceBase(distributorId)}/${type}/${id}`, data)).data;
export const deleteDistributorWorkspaceEntity = async (type, id, distributorId) =>
  (await client().delete(`${workspaceBase(distributorId)}/${type}/${id}`)).data;
export const uploadDistributorDocument = async ({ distributorId, tenantId, file, title, category, description, applicationId }) =>
  (await client().post(`${workspaceBase(distributorId)}/documents/upload`, file, {
    params: { tenantId, filename: file.name, mimeType: file.type || 'application/octet-stream', title, category, description, applicationId },
    headers: { 'Content-Type': 'application/octet-stream' },
  })).data;
export const getDistributorDocumentDownload = async (id, distributorId) =>
  (await client().get(`${workspaceBase(distributorId)}/documents/${id}/download`)).data;
export const extractDistributorDocument = async (id, distributorId) =>
  (await client().post(`${workspaceBase(distributorId)}/documents/${id}/extract`)).data;
export const uploadDistributorTemplate = async ({ distributorId, file, name, label, insurer, product, templateType, folderId, category, description, entityTypeTarget }) =>
  (await client().post(`${workspaceBase(distributorId)}/templates/upload`, file, {
    params: { filename: file.name, mimeType: file.type || 'application/octet-stream', name, label, insurer, product, templateType, folderId, category, description, entityTypeTarget },
    headers: { 'Content-Type': 'application/octet-stream' },
  })).data;
export const getDistributorTemplateDownload = async (id, distributorId) =>
  (await client().get(`${workspaceBase(distributorId)}/templates/${id}/download`)).data;
